import { nextTick, onUnmounted, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { positionOfState, readEntry, scrollKey, writeEntry } from '@/utils/scrollMemory'

/**
 * Remember and restore a scroll container's offset per history entry, so back
 * lands where the user left off and a push starts at the top — the way a native
 * app behaves. See `src/utils/scrollMemory.ts` for why the position has to be
 * kept here rather than by vue-router.
 */
export interface ScrollRestoreOptions<S> {
  /**
   * Distinguishes containers within one screen. Defaults to `main`, which is
   * what `AppScreen` uses; any screen with a second scroller needs its own.
   */
  id?: string
  /**
   * Screen state the remembered offset depends on. On the lookup screens a bare
   * `scrollTop` is meaningless after a remount: the search query has reset to
   * `''` and the paged window back to 50 of 4,991 rows, so "row 300 of the
   * filtered list" would be restored into a list that is neither filtered nor
   * 300 rows long. Captured alongside the offset and reapplied before it.
   */
  capture?: () => S
  restore?: (state: S) => void | Promise<void>
}

/**
 * Frames the restore keeps re-asserting the offset before giving up — roughly
 * 1.5 s at 60 Hz. A frame budget rather than a wall-clock deadline so tests are
 * deterministic without faking `performance.now`.
 */
const MAX_FRAMES = 90

/**
 * Input that means the user has taken over. Deliberately NOT `scroll`: our own
 * `scrollTop` writes emit `scroll`, so listening for it would cancel the restore
 * on its own first frame.
 */
const TAKEOVER_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const

export function useScrollRestore<S = undefined>(options: ScrollRestoreOptions<S> = {}) {
  const { id = 'main', capture, restore } = options

  const containerRef = shallowRef<HTMLElement | null>(null)
  const router = useRouter()
  const route = useRoute()

  function currentKey(): string {
    return scrollKey(positionOfState(router.options.history.state), route.fullPath, id)
  }

  // ---------------------------------------------------------------- save

  let savePending = false

  function save() {
    const el = containerRef.value
    if (!el) return
    writeEntry(currentKey(), { top: el.scrollTop, state: capture?.() })
  }

  /**
   * There is deliberately no save on unmount. By the time a screen unmounts
   * during a push, `history.state.position` and `route.fullPath` have already
   * advanced to the NEW entry, so a key computed there would file the old
   * screen's offset under the new screen's name. Saving while the entry is still
   * current sidesteps that entire class of bug, and costs at most one frame of
   * staleness — a user's last scroll always settles well before the tap that
   * navigates away.
   */
  function onScroll() {
    if (savePending) return
    savePending = true
    requestAnimationFrame(() => {
      savePending = false
      save()
    })
  }

  // ------------------------------------------------------------- restore

  let cancelTakeover: (() => void) | undefined

  function stopWatchingTakeover() {
    cancelTakeover?.()
    cancelTakeover = undefined
  }

  /**
   * A restore can be started while an earlier one is still waiting for content —
   * the element watcher and `afterEach` both trigger it, and a slow-loading
   * screen keeps the first attempt alive for up to `MAX_FRAMES`. Stamping each
   * attempt means the older frame loop retires instead of running alongside the
   * newer one and writing a stale target.
   */
  let generation = 0

  async function attemptRestore() {
    const el = containerRef.value
    if (!el) return

    const entry = readEntry(currentKey())
    if (!entry) return // A fresh push has nothing stored: start at the top.

    if (entry.state !== undefined && restore) {
      // Before the offset, not after: on `LookupStopsScreen` this is what widens
      // the paged window back out, and until it does, the rows the offset points
      // at do not exist and the assignment below would clamp to the bottom.
      await restore(entry.state as S)
      await nextTick()
      if (containerRef.value !== el) return
    }

    const target = entry.top
    if (target <= 0) return

    const attempt = ++generation
    let aborted = false
    stopWatchingTakeover()
    const abort = () => {
      aborted = true
      stopWatchingTakeover()
    }
    for (const event of TAKEOVER_EVENTS) el.addEventListener(event, abort, { passive: true })
    cancelTakeover = () => {
      for (const event of TAKEOVER_EVENTS) el.removeEventListener(event, abort)
    }

    /**
     * Re-asserted every frame rather than assigned once, because the screen is
     * still filling in. vue-router runs `scrollBehavior` one tick after mount,
     * when `useTransitIndex` is still rendering "Loading network data…" and the
     * container is a single line tall; assigning `scrollTop = 8000` there
     * silently clamps to 0 and the value is gone. The first assignment happens
     * synchronously inside `onMounted`, before the browser paints, so when the
     * content IS already present the user never sees the top.
     */
    let frames = 0
    const settle = () => {
      if (attempt !== generation) return // Superseded; the newer attempt owns the element.
      if (aborted || containerRef.value !== el) return stopWatchingTakeover()

      el.scrollTop = target

      const reachable = el.scrollHeight - el.clientHeight >= target
      if (reachable || ++frames >= MAX_FRAMES) return stopWatchingTakeover()

      requestAnimationFrame(settle)
    }
    settle()
  }

  // ------------------------------------------------------------ lifecycle

  /**
   * Bound to the element rather than to `onMounted`, because several of these
   * containers are themselves conditional — `LookupRoutesScreen`'s list is a
   * `v-else-if` on loaded data, the planner's results pane a `v-else` on the
   * pin-picking map. At mount they do not exist yet, so a mount-time restore
   * would find no element and silently do nothing. `flush: 'post'` so the
   * element is in the document when we measure it.
   */
  const stopElementWatch = watch(
    containerRef,
    (el, previous) => {
      previous?.removeEventListener('scroll', onScroll)
      if (!el) return
      el.addEventListener('scroll', onScroll, { passive: true })
      void attemptRestore()
    },
    { immediate: true, flush: 'post' },
  )

  /**
   * `onMounted` alone is not enough. RouterView REUSES the component instance
   * when only params change, so back-navigating `/stops/2/buses` →
   * `/stops/1/buses` never remounts `StopBusesScreen`. `afterEach` fires on every
   * confirmed navigation regardless; `nextTick` lets the DOM settle first, and
   * `attemptRestore` no-ops when the key has nothing stored.
   */
  const stopAfterEach = router.afterEach(() => {
    void nextTick(attemptRestore)
  })

  /** A query typed but never scrolled still has to survive the trip away. */
  const stopStateWatch = capture ? watch(capture, save, { deep: true }) : undefined

  onUnmounted(() => {
    containerRef.value?.removeEventListener('scroll', onScroll)
    stopWatchingTakeover()
    stopElementWatch()
    stopAfterEach()
    stopStateWatch?.()
  })

  return { containerRef }
}
